import { chatIdentifiersEqual, createArgsFromSchema, installationLocationsEqual, shownArgs, shownArgsAreValid, } from "@shared";
import { builtinBot } from "../utils/builtinBotCommands";
import { communitiesStore, currentUserIdStore } from "./app/stores";
function filterCommand(formatter, c, selectedCommand, parsedPrefix, prefixParts) {
    if (c.devmode && import.meta.env.OC_NODE_ENV === "production")
        return false;
    if (selectedCommand !== undefined) {
        return commandsMatch(selectedCommand, c);
    }
    if (prefixParts.length > 1) {
        return c.name.toLocaleLowerCase() === parsedPrefix;
    }
    else {
        const desc = c.description ? formatter(c.description).toLocaleLowerCase() : undefined;
        return (c.name.toLocaleLowerCase().includes(parsedPrefix) ||
            (desc?.includes(parsedPrefix) ?? false));
    }
}
function parseCommand(input) {
    const regex = /"([^"]+)"|(\S+)/g;
    const result = [];
    let match;
    while ((match = regex.exec(input)) !== null) {
        if (match[1]) {
            result.push(match[1]);
        }
        else if (match[2]) {
            result.push(match[2]);
        }
    }
    return result;
}
function sortCommands(prefix) {
    return (a, b) => {
        if (prefix.length === 0) {
            const compareBotNames = a.botName.localeCompare(b.botName);
            if (compareBotNames !== 0) {
                return compareBotNames;
            }
        }
        const aStartsWithPrefix = a.name.toLocaleLowerCase().startsWith(prefix);
        const bStartsWithPrefix = b.name.toLocaleLowerCase().startsWith(prefix);
        if (aStartsWithPrefix && !bStartsWithPrefix) {
            return -1;
        }
        else if (!aStartsWithPrefix && bStartsWithPrefix) {
            return 1;
        }
        else {
            return a.name.localeCompare(b.name);
        }
    };
}
function commandsMatch(a, b) {
    if (a === undefined || b === undefined)
        return false;
    return a.botName === b.botName && a.name === b.name;
}
export class BotState {
    #messageFormatter = $state((s) => s);
    #error = $state();
    #prefix = $state("");
    #selectedCommand = $state();
    #focusedCommandIndex = $state(0);
    #selectedCommandArgs = $state([]);
    #externalBots = $state(new Map());
    #showingBuilder = $state();
    #prefixParts = $derived(parseCommand(this.#prefix));
    #maybeArgs = $derived(this.#prefixParts.slice(1) ?? []);
    #parsedPrefix = $derived(this.#prefixParts[0]?.slice(1)?.toLocaleLowerCase() ?? "");
    #commands = $derived.by(() => {
        const prefixParts = this.#prefixParts;
        const parsedPrefix = this.#parsedPrefix;
        const bots = [builtinBot, ...this.#externalBots.values()];
        return bots
            .flatMap((b) => {
            switch (b.kind) {
                case "external_bot":
                    return b.definition.commands
                        .map((c) => {
                        return {
                            ...c,
                            kind: b.kind,
                            botName: b.name,
                            avatarUrl: b.avatarUrl,
                            botId: b.id,
                            botEndpoint: b.endpoint,
                            botDescription: b.definition.description,
                        };
                    })
                        .filter((c) => filterCommand(this.#messageFormatter, c, this.#selectedCommand, parsedPrefix, prefixParts));
                case "internal_bot":
                    return b.definition.commands
                        .map((c) => {
                        return {
                            ...c,
                            kind: b.kind,
                            botName: b.name,
                            botDescription: b.definition.description,
                        };
                    })
                        .filter((c) => filterCommand(this.#messageFormatter, c, this.#selectedCommand, parsedPrefix, prefixParts));
            }
        })
            .sort(sortCommands(this.#parsedPrefix));
    });
    #instanceValid = $derived.by(() => {
        if (this.#selectedCommand === undefined)
            return false;
        return this.instanceIsValid(this.#selectedCommand, this.#selectedCommandArgs);
    });
    set messageFormatter(v) {
        this.#messageFormatter = v;
    }
    get focusedCommandIndex() {
        return this.#focusedCommandIndex;
    }
    get instanceValid() {
        return this.#instanceValid;
    }
    get showingBuilder() {
        return this.#showingBuilder;
    }
    get error() {
        return this.#error;
    }
    set error(err) {
        this.#error = err;
    }
    get commands() {
        return this.#commands;
    }
    get selectedCommand() {
        return this.#selectedCommand;
    }
    get selectedCommandArgs() {
        return this.#selectedCommandArgs;
    }
    get prefix() {
        return this.#prefix;
    }
    set prefix(v) {
        this.#prefix = v;
    }
    get prefixParts() {
        return this.#prefixParts;
    }
    get parsedPrefix() {
        return this.#parsedPrefix;
    }
    focusPreviousCommand() {
        this.#focusedCommandIndex = (this.#focusedCommandIndex + 1) % this.#commands.length;
    }
    focusNextCommand() {
        this.#focusedCommandIndex =
            (this.#focusedCommandIndex - 1 + this.#commands.length) % this.#commands.length;
    }
    setSelectedCommand(messageContext, commands, cmd) {
        cmd = cmd ?? commands[this.#focusedCommandIndex];
        // make sure that we don't set the same command twice
        if (!commandsMatch(this.#selectedCommand, cmd)) {
            this.#selectedCommand = cmd;
            if (cmd !== undefined) {
                this.#focusedCommandIndex = 0;
                if (cmd.params.length > 0) {
                    this.#selectedCommandArgs = createArgsFromSchema(cmd.params, this.#maybeArgs);
                }
                // if the instance is not already valid (via inline params) show the builder modal
                this.#showingBuilder = !this.#instanceValid ? messageContext : undefined;
            }
        }
        return this.#selectedCommand;
    }
    cancel() {
        this.#selectedCommand = undefined;
        this.#error = undefined;
        this.#prefix = "";
        this.#focusedCommandIndex = 0;
        this.#selectedCommandArgs = [];
        this.#showingBuilder = undefined;
    }
    createBotInstance(command) {
        switch (command.kind) {
            case "external_bot":
                return {
                    kind: "external_bot",
                    id: command.botId,
                    endpoint: command.botEndpoint,
                    command: {
                        name: command.name,
                        arguments: shownArgs(command.params, this.#selectedCommandArgs),
                        placeholder: command.placeholder,
                    },
                };
            case "internal_bot":
                return {
                    kind: "internal_bot",
                    command: {
                        name: command.name,
                        arguments: shownArgs(command.params, this.#selectedCommandArgs),
                    },
                };
        }
    }
    instanceIsValid(command, params) {
        if (params.length !== command.params.length) {
            return false;
        }
        return shownArgsAreValid(command.params, params);
    }
    get externalBots() {
        return this.#externalBots;
    }
    setExternalBots(bots) {
        const map = new Map();
        bots.forEach((b) => map.set(b.id, b));
        this.#externalBots = map;
    }
}
export const botState = new BotState();
export function hydrateBots(installed, allBots) {
    return [...installed.entries()].reduce((bots, [id, perm]) => {
        const bot = allBots.get(id);
        if (bot !== undefined) {
            bots.push({
                ...bot,
                grantedPermissions: perm,
            });
        }
        return bots;
    }, []);
}
export function botIsInstallable(bot, location) {
    if (bot.definition.restrictedLocations?.includes(location.kind) === false) {
        return false;
    }
    switch (bot.registrationStatus.kind) {
        case "public":
            return true;
        case "private": {
            return (bot.ownerId === currentUserIdStore.value ||
                (bot.registrationStatus.location !== undefined &&
                    installationLocationsEqual(bot.registrationStatus.location, location)));
        }
    }
}
export function botContainerFrom(collection) {
    switch (collection.kind) {
        case "channel":
            return [...communitiesStore.value.values()].find((c) => {
                return (c.channels.findIndex((ch) => chatIdentifiersEqual(ch.id, collection.id)) >= 0);
            });
        case "direct_chat":
            return { ...collection, id: { kind: "direct_chat", userId: currentUserIdStore.value } };
        default:
            return collection;
    }
}
export function installationLocationFrom(collection) {
    switch (collection.kind) {
        case "channel":
            return { kind: "community", communityId: collection.id.communityId };
        case "direct_chat":
            return { kind: "direct_chat", userId: currentUserIdStore.value };
        default:
            return collection.id;
    }
}
